import {
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  RespondToAuthChallengeCommand,
  SignUpCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import {
  createSrpSession,
  signSrpSession,
  wrapAuthChallenge,
  wrapInitiateAuth,
} from 'cognito-srp-helper';
import type { AuthConfig } from '@quizdb/common';
import {
  AuthError,
  type AuthErrorCode,
  type CognitoAuthClient,
  type CognitoTokens,
} from './authServices';
import {
  clearPendingSignIn,
  createPkcePair,
  createState,
  loadPendingSignIn,
  savePendingSignIn,
} from './pkce';

/** QZ-D28's Cognito exception name -> `AuthErrorCode` table. Anything absent here is `Unknown`. */
const ERROR_CODE_BY_EXCEPTION: Record<string, AuthErrorCode> = {
  NotAuthorizedException: 'NotAuthorized',
  UserNotConfirmedException: 'UserNotConfirmed',
  UsernameExistsException: 'UsernameExists',
  CodeMismatchException: 'CodeMismatch',
  ExpiredCodeException: 'ExpiredCode',
  InvalidPasswordException: 'InvalidPassword',
  UserLambdaValidationException: 'SignUpNotAllowed',
  LimitExceededException: 'LimitExceeded',
  TooManyRequestsException: 'LimitExceeded',
};

/** An `AuthError` passes through unchanged; anything else is mapped by its exception name. */
const toAuthError = (thrown: unknown): AuthError => {
  if (thrown instanceof AuthError) return thrown;
  const name = thrown instanceof Error ? thrown.name : undefined;
  const code = (name !== undefined ? ERROR_CODE_BY_EXCEPTION[name] : undefined) ?? 'Unknown';
  const message = thrown instanceof Error ? thrown.message : undefined;
  return new AuthError(code, message);
};

const runGuarded = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn();
  } catch (thrown) {
    throw toAuthError(thrown);
  }
};

export interface CognitoAuthClientDeps {
  client?: CognitoIdentityProviderClient;
  fetch?: typeof fetch;
  location?: Pick<Location, 'assign' | 'origin'>;
  storage?: Storage;
}

/** QZ-D28: the browser's own calls to the user pool, no hosted UI and no AWS credentials. */
export const createCognitoAuthClient = (
  config: AuthConfig,
  deps: CognitoAuthClientDeps = {}
): CognitoAuthClient => {
  const client = deps.client ?? new CognitoIdentityProviderClient({ region: config.region });
  const fetchFn = deps.fetch ?? globalThis.fetch.bind(globalThis);
  const location = deps.location ?? window.location;
  const storage = deps.storage ?? sessionStorage;

  const signIn = (email: string, password: string): Promise<CognitoTokens> =>
    runGuarded(async () => {
      const srpSession = createSrpSession(email, password, config.userPoolId, false);
      const initiateAuthRes = await client.send(
        new InitiateAuthCommand(
          wrapInitiateAuth(srpSession, {
            ClientId: config.clientId,
            AuthFlow: 'USER_SRP_AUTH',
            AuthParameters: {
              USERNAME: email,
            },
          })
        )
      );
      if (
        initiateAuthRes.ChallengeName !== 'PASSWORD_VERIFIER' ||
        initiateAuthRes.ChallengeParameters === undefined
      ) {
        throw new AuthError('Unknown');
      }
      const signedSrpSession = signSrpSession(srpSession, initiateAuthRes);
      // Sign-in is by email alias: the challenge response's USERNAME must be the pool's
      // internal id, not the alias, or Cognito refuses the password signature.
      const userIdForSrp = initiateAuthRes.ChallengeParameters.USER_ID_FOR_SRP;
      if (userIdForSrp === undefined) {
        throw new AuthError('Unknown');
      }
      const respondRes = await client.send(
        new RespondToAuthChallengeCommand(
          wrapAuthChallenge(signedSrpSession, {
            ClientId: config.clientId,
            ChallengeName: 'PASSWORD_VERIFIER',
            Session: initiateAuthRes.Session,
            ChallengeResponses: {
              USERNAME: userIdForSrp,
            },
          })
        )
      );
      const result = respondRes.AuthenticationResult;
      if (
        result?.AccessToken === undefined ||
        result.RefreshToken === undefined ||
        result.ExpiresIn === undefined
      ) {
        throw new AuthError('Unknown');
      }
      return {
        accessToken: result.AccessToken,
        refreshToken: result.RefreshToken,
        expiresAt: Date.now() + result.ExpiresIn * 1000,
      };
    });

  const signUp = (email: string, password: string): Promise<void> =>
    runGuarded(async () => {
      await client.send(
        new SignUpCommand({
          ClientId: config.clientId,
          Username: email,
          Password: password,
          UserAttributes: [{ Name: 'email', Value: email }],
        })
      );
    });

  const confirmSignUp = (email: string, code: string): Promise<void> =>
    runGuarded(async () => {
      await client.send(
        new ConfirmSignUpCommand({
          ClientId: config.clientId,
          Username: email,
          ConfirmationCode: code,
        })
      );
    });

  const resendConfirmationCode = (email: string): Promise<void> =>
    runGuarded(async () => {
      await client.send(
        new ResendConfirmationCodeCommand({
          ClientId: config.clientId,
          Username: email,
        })
      );
    });

  const forgotPassword = (email: string): Promise<void> =>
    runGuarded(async () => {
      await client.send(
        new ForgotPasswordCommand({
          ClientId: config.clientId,
          Username: email,
        })
      );
    });

  const confirmForgotPassword = (email: string, code: string, newPassword: string): Promise<void> =>
    runGuarded(async () => {
      await client.send(
        new ConfirmForgotPasswordCommand({
          ClientId: config.clientId,
          Username: email,
          ConfirmationCode: code,
          Password: newPassword,
        })
      );
    });

  const startGoogleSignIn = (): Promise<void> =>
    runGuarded(async () => {
      const { verifier, challenge } = await createPkcePair();
      const state = createState();
      const redirectUri = `${location.origin}/auth/google/callback`;
      savePendingSignIn({ verifier, state, redirectUri }, storage);

      const authorizeUrl = new URL(`https://${config.domain}/oauth2/authorize`);
      authorizeUrl.searchParams.set('identity_provider', 'Google');
      authorizeUrl.searchParams.set('response_type', 'code');
      authorizeUrl.searchParams.set('client_id', config.clientId);
      authorizeUrl.searchParams.set('redirect_uri', redirectUri);
      authorizeUrl.searchParams.set('scope', 'openid email profile');
      authorizeUrl.searchParams.set('code_challenge', challenge);
      authorizeUrl.searchParams.set('code_challenge_method', 'S256');
      authorizeUrl.searchParams.set('state', state);

      location.assign(authorizeUrl.toString());
    });

  const completeGoogleSignIn = (callbackUrl: string): Promise<CognitoTokens> =>
    runGuarded(async () => {
      // Single-use: cleared on every callback, whatever it holds, before anything else runs.
      const pending = loadPendingSignIn(storage);
      clearPendingSignIn(storage);

      const params = new URL(callbackUrl).searchParams;
      const error = params.get('error');
      if (error !== null) {
        const description = params.get('error_description') ?? '';
        throw new AuthError(
          /PreSignUp/i.test(description) ? 'SignUpNotAllowed' : 'GoogleSignInFailed'
        );
      }

      const code = params.get('code');
      const state = params.get('state');
      if (pending === undefined || code === null || state !== pending.state) {
        throw new AuthError('GoogleSignInFailed');
      }

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        code,
        redirect_uri: pending.redirectUri,
        code_verifier: pending.verifier,
      });
      const response = await fetchFn(`https://${config.domain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!response.ok) {
        throw new AuthError('GoogleSignInFailed');
      }
      const payload = (await response.json()) as {
        access_token?: string;
        refresh_token?: string;
        expires_in?: number;
      };
      if (
        payload.access_token === undefined ||
        payload.refresh_token === undefined ||
        payload.expires_in === undefined
      ) {
        throw new AuthError('GoogleSignInFailed');
      }
      return {
        accessToken: payload.access_token,
        refreshToken: payload.refresh_token,
        expiresAt: Date.now() + payload.expires_in * 1000,
      };
    });

  return {
    signIn,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmForgotPassword,
    startGoogleSignIn,
    completeGoogleSignIn,
  };
};
